import {
  AppConfig,
  useSubscriptionAccessConfigContext,
} from '@ltvco/refresh-lib/ctx';
import { capitalizeEachWord } from '@ltvco/refresh-lib/utils';
import {
  useSearchAllAccessAddon,
  type AccountResponse,
} from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  DarkWebScan,
  FamilyThree,
  FraudScan,
  MemberPerks,
  Monitoring,
  UnclaimedMoney,
} from 'icons';

const PHONE_ONLY_GATED_FEATURES = [
  'identity_alerts',
  'monitor_reports',
  'id_theft_insurance',
];

interface UseExploreNewFeaturesArgs {
  account?: AccountResponse | null;
  openFeatureBlockedModal: () => void;
  setSearchType: (searchType: string) => void;
  showLeaveFeedbackFeature?: boolean;
}

export function useExploreNewFeatures({
  account,
  openFeatureBlockedModal,
  setSearchType,
  showLeaveFeedbackFeature = false,
}: UseExploreNewFeaturesArgs) {
  const navigate = useNavigate();
  const { isBlocked, isHidden, phoneOnlyVariation } =
    useSubscriptionAccessConfigContext();
  const { shouldOfferSearchAllAccess } = useSearchAllAccessAddon();
  const inUCMAddonVariation = useFeatureIsOn('RTRN-1656');
  const { trackEventGA4 } = useContext(AppConfig);
  const [isFeedbackSurveyModalOpen, setIsFeedbackSurveyModalOpen] =
    useState(false);

  const hasPurchasedUCMAddon =
    account?.account?.subscription_info?.one_time_ucm_purchased;
  const shouldDisplayUcmAddonModal =
    !!account && inUCMAddonVariation && !hasPurchasedUCMAddon;

  const featureAction = useCallback(
    (feature: string) => {
      if (feature === 'unclaimed money' && shouldDisplayUcmAddonModal) {
        trackEventGA4({
          event_name: 'engagement_click',
          type: 'cta_dashboard_promo_card',
          subtype: 'ucm_upsell',
        });
      }

      if (feature === 'dark web' && isBlocked('dark_web')) {
        trackEventGA4({
          event_name: 'engagement_click',
          type: 'darkwebscan_dashboard',
          section_name: 'darkweb_upsell',
        });
      }

      setSearchType(capitalizeEachWord(feature));
      openFeatureBlockedModal();
    },
    [
      shouldDisplayUcmAddonModal,
      isBlocked,
      trackEventGA4,
      setSearchType,
      openFeatureBlockedModal,
    ]
  );

  const allFeatures = useMemo(
    () => [
      {
        title: 'Family Tree',
        description:
          'Start building your family tree! Organize your information now',
        icon: <FamilyThree />,
        key: 'ancestry',
        action: () =>
          isBlocked('ancestry') || shouldOfferSearchAllAccess
            ? featureAction('family-tree')
            : navigate('/family-tree'),
      },
      {
        title: 'Unclaimed Money',
        description: 'Find out what money you may be owed',
        icon: <UnclaimedMoney />,
        key: 'unclaimed_money',
        action: () =>
          isBlocked('unclaimed_money') || shouldDisplayUcmAddonModal
            ? featureAction('unclaimed money')
            : navigate('/dashboard/unclaimed-money'),
      },
      {
        title: 'Dark Web Scan',
        description: 'Check if your email was potentially leaked',
        icon: <DarkWebScan />,
        key: 'dark_web',
        action: () =>
          isBlocked('dark_web') || shouldOfferSearchAllAccess
            ? featureAction('dark web')
            : navigate('/dashboard/dark-web'),
      },
      {
        title: 'Monitor Your Reports',
        description: 'Receive updates if we detect changes',
        icon: <Monitoring />,
        action: () => navigate('/dashboard/monitoring?tab=monitoredReports'),
        key: 'monitor_reports',
      },
      {
        title: 'Member Perks',
        description:
          'Savings may be available through gift cards, discounts, and more',
        icon: <MemberPerks />,
        action: () => navigate('/dashboard/rewards'),
      },
      showLeaveFeedbackFeature
        ? {
            title: 'Leave Feedback',
            description: 'Share your feedback with us',
            icon: <QuestionAnswerOutlinedIcon sx={{ color: 'primary.main' }} />,
            key: 'leave_feedback',
            action: () => {
              trackEventGA4({
                event_name: 'engagement_click',
                section_name: 'my_account_feature',
                type: 'leave_feedback',
              });
              setIsFeedbackSurveyModalOpen(true);
            },
          }
        : {
            title: 'FraudScan',
            description:
              'Check phone numbers and emails for threats and fraudulent info',
            icon: <FraudScan />,
            key: 'fraud_scan',
            action: () =>
              isBlocked('fraud_scan') || shouldOfferSearchAllAccess
                ? featureAction('fraud scan')
                : navigate('/dashboard/fraud'),
          },
    ],
    [
      isBlocked,
      navigate,
      shouldDisplayUcmAddonModal,
      shouldOfferSearchAllAccess,
      showLeaveFeedbackFeature,
      trackEventGA4,
      featureAction,
    ]
  );

  const features = useMemo(
    () =>
      allFeatures.filter((feature) => {
        if (feature.key && isHidden(feature.key)) {
          return false;
        }
        if (
          feature.key &&
          PHONE_ONLY_GATED_FEATURES.includes(feature.key) &&
          phoneOnlyVariation
        ) {
          return false;
        }
        return true;
      }),
    [allFeatures, isHidden, phoneOnlyVariation]
  );

  return {
    features,
    shouldDisplayUcmAddonModal,
    isFeedbackSurveyModalOpen,
    closeFeedbackSurveyModal: () => setIsFeedbackSurveyModalOpen(false),
  };
}
